/**
 * uploads.ts — one source of truth for the Knowledge-Hub file-upload size limit,
 * shared by the browser (pre-check before we base64-encode + POST) and the Worker
 * (defense-in-depth guard after decoding). Files go up as base64 inside a JSON body,
 * which inflates them ~33% and must fit in the Worker's request/memory budget, so a
 * founder dropping a big video needs to hear a plain-English "too big" up front —
 * not a UI freeze followed by a cryptic failure.
 */

/** Hard ceiling for a single Knowledge upload, in bytes (25 MB). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Human label for the limit, e.g. shown in the dropzone hint ("Files up to 25 MB"). */
export const MAX_UPLOAD_LABEL = '25 MB';

/** Round a byte count to a short, founder-friendly size string (e.g. "1.4 MB", "812 KB"). */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 KB';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  // one decimal under 10 MB, whole numbers above — keeps it glanceable
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`;
}

/**
 * Returns a plain-English error message if `bytes` is over the limit (or not a real
 * size), else null. Same message on both sides so a founder reads one consistent line
 * whether the browser catches it first or the server does.
 */
export function uploadSizeError(bytes: number): string | null {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return 'That file looks empty — try a different one.';
  }
  if (bytes > MAX_UPLOAD_BYTES) {
    return `That file is ${formatFileSize(bytes)} — too big to file here (limit ${MAX_UPLOAD_LABEL}). For a large video, drop a link in a note instead.`;
  }
  return null;
}
